// src/features/encounter/encounterItemSlice.ts

import { createSlice } from "@reduxjs/toolkit"
import { EncounterWIthItemInvoiceSerializer } from "@/types/encounterItem"
import { EncounterHistoryPagination, fetchEncounterHistory } from "@core/api/encounterHistoryApi"

interface EncounterHistoryState {
  encounterHistories: EncounterWIthItemInvoiceSerializer[]
  loading: boolean
  loadingMore: boolean
  error: string | null
  currentPatientUuid: string | null
  currentRequestId: string | null
  foregroundRequestId: string | null
  foregroundPatientUuid: string | null
  historyGeneration: number
  appendRequestId: string | null
  appendGeneration: number | null
  pagination: EncounterHistoryPagination
}

const initialState: EncounterHistoryState = {
  encounterHistories: [],
  loading: false,
  loadingMore: false,
  error: null,
  currentPatientUuid: null,
  currentRequestId: null,
  foregroundRequestId: null,
  foregroundPatientUuid: null,
  historyGeneration: 0,
  appendRequestId: null,
  appendGeneration: null,
  pagination: {
    hasMore: false,
    nextBeforeId: null,
  },
}

const encounterHistorySlice = createSlice({
  name: "encounterHistory",
  initialState,
  reducers: {
    // 모든 아이템을 초기화할 때
    clearEncounterHistories(state) {
      state.encounterHistories = []
      state.loading = false
      state.loadingMore = false
      state.error = null
      state.currentPatientUuid = null
      state.currentRequestId = null
      state.foregroundRequestId = null
      state.foregroundPatientUuid = null
      state.historyGeneration = 0
      state.appendRequestId = null
      state.appendGeneration = null
      state.pagination = {
        hasMore: false,
        nextBeforeId: null,
      }
    },
  },
  extraReducers: builder => {
    builder
      // fetchEncounterHistory 관련 처리
      .addCase(fetchEncounterHistory.pending, (state, action) => {
        const isAppend = Boolean(action.meta.arg.append || action.meta.arg.beforeId)
        if (isAppend) {
          state.loadingMore = true
          state.appendRequestId = action.meta.requestId
          state.appendGeneration = state.historyGeneration
        } else {
          if (action.meta.arg.silent && state.foregroundPatientUuid && state.foregroundPatientUuid !== action.meta.arg.uuid) {
            state.loading = false
            state.foregroundRequestId = null
            state.foregroundPatientUuid = null
          }
          if (!action.meta.arg.silent) {
            state.loading = true
          }
          state.loadingMore = false
          state.appendRequestId = null
          state.appendGeneration = null
          state.historyGeneration += 1
          if (!action.meta.arg.silent) {
            state.error = null
          }
          state.currentRequestId = action.meta.requestId
          if (!action.meta.arg.silent) {
            state.foregroundRequestId = action.meta.requestId
            state.foregroundPatientUuid = action.meta.arg.uuid
          }
          state.currentPatientUuid = action.meta.arg.uuid
        }
      })
      .addCase(fetchEncounterHistory.fulfilled, (state, action) => {
        const { data, pagination, append } = action.payload

        const isAppend = Boolean(append)

        if (isAppend) {
          if (
            state.currentPatientUuid !== action.meta.arg.uuid ||
            state.appendRequestId !== action.meta.requestId ||
            state.appendGeneration !== state.historyGeneration
          ) {
            return
          }
        } else {
          const ownsRequest = action.meta.arg.silent
            ? state.currentRequestId === action.meta.requestId
            : state.foregroundRequestId === action.meta.requestId
          if (!ownsRequest || state.currentPatientUuid !== action.meta.arg.uuid) {
            return
          }
        }

        if (isAppend) {
          state.loadingMore = false
          state.error = null
          const existing = new Set(state.encounterHistories.map(history => history.encounterUuid || ""))
          const incoming: EncounterWIthItemInvoiceSerializer[] = []

          data.forEach(history => {
            const key = history.encounterUuid || ""
            if (key && existing.has(key)) {
              return
            }
            if (key) {
              existing.add(key)
            }
            incoming.push(history)
          })

          state.encounterHistories = [...state.encounterHistories, ...incoming]
          state.appendRequestId = null
          state.appendGeneration = null
        } else {
          state.loading = false
          state.loadingMore = false
          state.error = null
          if (action.meta.arg.silent) {
            state.currentRequestId = null
            state.foregroundRequestId = null
            state.foregroundPatientUuid = null
          } else {
            state.foregroundRequestId = null
            state.foregroundPatientUuid = null
            if (state.currentRequestId === action.meta.requestId) {
              state.currentRequestId = null
            }
          }
          const seenEncounterUuids = new Set<string>()
          // encounterUuid 기준으로 중복 진료 제거
          state.encounterHistories = data.filter(history => {
            const key = history.encounterUuid

            if (!key) {
              return true
            }

            if (seenEncounterUuids.has(key)) {
              return false
            }

            seenEncounterUuids.add(key)
            return true
          })
        }

        const hasMore = pagination?.hasMore ?? (pagination as any)?.has_more ?? false
        const nextBeforeId = pagination?.nextBeforeId ?? (pagination as any)?.next_before_id ?? null

        state.pagination = {
          hasMore,
          nextBeforeId,
          limit: pagination?.limit ?? (pagination as any)?.limit,
        }
      })
      .addCase(fetchEncounterHistory.rejected, (state, action) => {
        const isAppend = Boolean(action.meta.arg.append || action.meta.arg.beforeId)
        if (isAppend) {
          if (
            state.currentPatientUuid === action.meta.arg.uuid &&
            state.appendRequestId === action.meta.requestId &&
            state.appendGeneration === state.historyGeneration
          ) {
            state.loadingMore = false
            state.appendRequestId = null
            state.appendGeneration = null
          }
        } else {
          const ownsRequest = action.meta.arg.silent
            ? state.currentRequestId === action.meta.requestId
            : state.foregroundRequestId === action.meta.requestId
          if (!ownsRequest || state.currentPatientUuid !== action.meta.arg.uuid) {
            return
          }

          if (action.meta.arg.silent) {
            state.currentRequestId = null
            state.loading = state.foregroundRequestId !== null && state.foregroundPatientUuid === state.currentPatientUuid
          } else {
            state.loading = false
            state.foregroundRequestId = null
            state.foregroundPatientUuid = null
            if (state.currentRequestId === action.meta.requestId) {
              state.currentRequestId = null
            }
          }
          if (!action.meta.arg.silent) {
            state.error = (action.payload as string) || action.error.message || "알 수 없는 오류 발생"
          }
        }
      })
  },
})

export const { clearEncounterHistories } = encounterHistorySlice.actions

export default encounterHistorySlice.reducer

